import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { Doc } from '@day';
import { db, functions } from '../firebase';

/**
 * Every write the control room makes, field for field as the phone's services make them, so a courier's phone, the
 * server and the rules see nothing new: SentRoutesService (lib/services/routes/sent_routes.dart), SentStopsService
 * (sent_stops.dart), LiveAlertsService (live_alerts.dart) and SettlingService (settling.dart): each of these is
 * something an owner may already do from the phone. The one new record is the stops waiting for a route
 * (`businesses/{id}/waitingStops`, docs/waiting-stops.md), with its own rule.
 */

// ── Routes a business sends (SentRoute.sendFields) ───────────────────────

export interface SendRoute {
  businessId: string;
  riderId: string;
  /** Stored as the phone stores them (`storedStop`: DeliveryStop.toJson). */
  stops: Doc[];
  optimization: Doc;
  name?: string | null;
  start?: { label: string; lat: number; lng: number } | null;
  /** When Google's positions in it have to go, if they're older than now (a stop from an earlier route). */
  googleUntil?: number | null;
  /** The note for the whole route (control/day-note), not one stop's: the courier sees it on the card and as they run it. */
  note?: string | null;
}

/** The longest route note the rules take (sent_routes.dart `routeNoteMax`). */
export const ROUTE_NOTE_MAX = 500;

/** A route note as it's kept: trimmed, at most [ROUTE_NOTE_MAX]; null when nothing's left (`cleanRouteNote`). */
export function cleanRouteNote(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim();
  return t ? t.slice(0, ROUTE_NOTE_MAX).trim() : null;
}

export function sendFields(r: SendRoute): Doc {
  return {
    status: 'sent',
    businessId: r.businessId,
    sentBy: r.businessId,
    riderId: r.riderId,
    ...(r.name && r.name.trim() ? { name: r.name.trim() } : {}),
    ...(r.start ? { start: { label: r.start.label, lat: r.start.lat, lng: r.start.lng } } : {}),
    stops: r.stops,
    optimization: r.optimization,
    sentAt: serverTimestamp(),
    ...(cleanRouteNote(r.note) ? { note: cleanRouteNote(r.note), noteAt: serverTimestamp() } : {}),
    // The server dates Google's positions from now unless this says earlier (google_retention.ts `stampNew`).
    ...(r.googleUntil ? { googleUntil: Timestamp.fromMillis(r.googleUntil) } : {}),
  };
}

/**
 * The note for the whole route, changed on its own while the route waits or runs (the rules allow only the note on a
 * started route); null removes it. The courier's phone never writes over it: its own writes name their fields.
 */
export const setRouteNote = (routeId: string, note: string | null) =>
  updateDoc(doc(db, 'routes', routeId), cleanRouteNote(note) ? { note: cleanRouteNote(note), noteAt: serverTimestamp() } : { note: deleteField(), noteAt: deleteField() });

/** A new route record's reference, made before it's written so Undo and the page can name it. */
export const newRouteRef = () => doc(collection(db, 'routes'));

/**
 * The owner sends a planned route to a courier (SentRoutesService.send). Stops it took from the waiting list leave it
 * in the same write, so they're never on a route and waiting at once, nor lost if the send fails.
 */
export function sendRoute(ref: DocumentReference, r: SendRoute, waitingIds: string[] = [], fromWaiting?: Doc): Promise<void> {
  if (!waitingIds.length) return setDoc(ref, sendFields(r));
  // In a transaction: if another tab has sent any of these stops already, nothing is written and it says so.
  return runTransaction(db, async (tx) => {
    const refs = waitingIds.map((id) => doc(db, 'businesses', r.businessId, 'waitingStops', id));
    const snaps = await Promise.all(refs.map((x) => tx.get(x)));
    if (snaps.some((x) => !x.exists())) throw new WaitingGone();
    // Where each came from, so taking the route back (or its 2 days running out) puts them back in Waiting.
    tx.set(ref, { ...sendFields(r), ...(fromWaiting ? { fromWaiting } : {}) });
    for (const x of refs) tx.delete(x);
  });
}

/** A waiting stop on a route being sent has gone (sent from another tab, or removed). */
export class WaitingGone extends Error {
  constructor() {
    super('waiting-gone');
  }
}

/** A route waiting to start isn't as it was read: started or declined, taken back, or changed in another tab. */
export class RouteChanged extends Error {
  constructor(readonly why: 'gone' | 'started' | 'changed') {
    super(`route-${why}`);
  }
}

/**
 * Waiting stops added to a route waiting to start: its stops and their times again, where they came from (so Take back
 * returns them to Waiting) and when Google's positions in it must go, in the same write as they leave the list. In a
 * transaction: if another tab sent or removed any of them, or the route started or changed since [was] (its stop ids
 * before), nothing is written and the error says which.
 */
export function addToRoute(o: { businessId: string; routeId: string; was: string[]; stops: Doc[]; optimization: Doc; fromWaiting: Doc; googleUntil: number | null; waitingIds: string[] }): Promise<void> {
  return runTransaction(db, async (tx) => {
    const ref = doc(db, 'routes', o.routeId);
    const refs = o.waitingIds.map((id) => doc(db, 'businesses', o.businessId, 'waitingStops', id));
    const [route, ...snaps] = await Promise.all([tx.get(ref), ...refs.map((x) => tx.get(x))]);
    if (!route.exists()) throw new RouteChanged('gone');
    const d = route.data() as Doc;
    if (d.status !== 'sent') throw new RouteChanged(d.status === 'declined' ? 'changed' : 'started');
    const ids = (Array.isArray(d.stops) ? (d.stops as Doc[]) : []).map((s) => String(s.id));
    if (ids.join() !== o.was.join()) throw new RouteChanged('changed');
    if (snaps.some((x) => !x.exists())) throw new WaitingGone();
    tx.update(ref, {
      status: 'sent',
      stops: o.stops,
      optimization: o.optimization,
      fromWaiting: o.fromWaiting,
      ...(o.googleUntil ? { googleUntil: Timestamp.fromMillis(o.googleUntil) } : {}),
    });
    for (const x of refs) tx.delete(x);
  });
}

// ── Stops waiting for a route (docs/waiting-stops.md) ────────────────────

const waitingRef = (businessId: string, id: string) => doc(db, 'businesses', businessId, 'waitingStops', id);

/** Batches stay under Firestore's 500 writes. */
async function inBatches<T>(items: T[], write: (b: ReturnType<typeof writeBatch>, x: T) => void): Promise<void> {
  for (let i = 0; i < items.length; i += 400) {
    const b = writeBatch(db);
    for (const x of items.slice(i, i + 400)) write(b, x);
    await b.commit();
  }
}

/** New waiting stops, each its own record (waiting.ts `waitingFields`). */
export const addWaiting = (businessId: string, records: { id: string; fields: Doc }[]) =>
  inBatches(records, (b, r) => b.set(waitingRef(businessId, r.id), r.fields));

/** The day they're for, or none. */
export const setWaitingDay = (businessId: string, ids: string[], day: string | null) =>
  inBatches(ids, (b, id) => b.update(waitingRef(businessId, id), { day: day ?? deleteField(), changedAt: serverTimestamp() }));

/**
 * Waiting stops changed in one go (Change… on a selection): each its own fields, a stored stop's as `stop.type`. Null
 * takes a field of the record off (`byTime`, `couriers`); a stored stop keeps its null, as the phone stores it.
 */
export const changeWaiting = (businessId: string, changes: { id: string; fields: Doc }[]) =>
  inBatches(changes, (b, c) =>
    b.update(waitingRef(businessId, c.id), {
      ...Object.fromEntries(Object.entries(c.fields).map(([k, v]) => [k, v === null && !k.startsWith('stop.') ? deleteField() : v])),
      changedAt: serverTimestamp(),
    }),
  );

/**
 * A fresh position from Google, looked up again as they're planned: kept 29 days from now. A stop that had no place ID
 * takes the one found, with its address kept as written ([named]), so no one takes Google's position for the customer's own.
 */
export const refreshWaitingGoogle = (businessId: string, found: { id: string; lat: number; lng: number; until: number; named?: { placeId: string; written: string } | null }[]) =>
  inBatches(found, (b, f) => b.update(waitingRef(businessId, f.id), { google: { lat: f.lat, lng: f.lng }, googleUntil: Timestamp.fromMillis(f.until), changedAt: serverTimestamp(), ...(f.named ? { 'stop.placeId': f.named.placeId, 'stop.writtenAddress': f.named.written } : {}) }));

/** Off the list, customer and all. */
export const removeWaiting = (businessId: string, ids: string[]) => inBatches(ids, (b, id) => b.delete(waitingRef(businessId, id)));

/**
 * "Give it to someone else": the same route sent afresh as a new record, so the server tells the new courier
 * (SentRoutesService.sendTo).
 */
export async function sendTo(fresh: DocumentReference, old: Doc & { id: string }, riderId: string): Promise<void> {
  await writeBatch(db).set(fresh, sentAfresh(old, riderId)).delete(doc(db, 'routes', old.id)).commit();
}

const sentAfresh = (old: Doc, riderId: string): Doc => ({
  // Given to someone else, its stops from Waiting still go back there if it's taken back.
  ...(old.fromWaiting ? { fromWaiting: old.fromWaiting } : {}),
  ...sendFields({
    businessId: String(old.businessId),
    riderId,
    stops: old.stops as Doc[],
    optimization: old.optimization as Doc,
    name: (old.name as string | undefined) ?? null,
    start: (old.start as SendRoute['start']) ?? null,
    googleUntil: (old.googleUntil as Timestamp | undefined)?.toMillis?.() ?? null,
    note: typeof old.note === 'string' ? old.note : null,
  }),
});

/**
 * "Swap with…": two routes waiting to start, each sent afresh to the other's courier, what `sendTo` writes for each. In a
 * transaction: if either has started, gone or changed hands since it was read, nothing is written.
 */
export function swapSent(a: { id: string; fresh: DocumentReference; riderId: string }, b: { id: string; fresh: DocumentReference; riderId: string }): Promise<void> {
  return runTransaction(db, async (tx) => {
    const refA = doc(db, 'routes', a.id);
    const refB = doc(db, 'routes', b.id);
    const [x, y] = await Promise.all([tx.get(refA), tx.get(refB)]);
    if (!x.exists() || !y.exists()) throw new RouteChanged('gone');
    const dx = x.data() as Doc;
    const dy = y.data() as Doc;
    if (dx.status !== 'sent' || dy.status !== 'sent') throw new RouteChanged('started');
    if (dx.riderId !== a.riderId || dy.riderId !== b.riderId) throw new RouteChanged('changed');
    tx.set(a.fresh, sentAfresh(dx, b.riderId));
    tx.set(b.fresh, sentAfresh(dy, a.riderId));
    tx.delete(refA);
    tx.delete(refB);
  });
}

/**
 * "Take it back" (SentRoutesService.takeBack): only a route not started, by its business. Stops it took from Waiting go
 * back there in the same write ([restore]: their records, docs/waiting-stops.md).
 */
export function takeBack(routeId: string, restore?: { businessId: string; items: { id: string; fields: Doc }[] }): Promise<void> {
  if (!restore?.items.length) return deleteDoc(doc(db, 'routes', routeId));
  const b = writeBatch(db);
  b.delete(doc(db, 'routes', routeId));
  for (const x of restore.items) b.set(doc(db, 'businesses', restore.businessId, 'waitingStops', x.id), x.fields);
  return b.commit();
}

/** A route not started, in a new order or with stops moved: its stops and their times again. */
export const reorderSent = (routeId: string, stops: Doc[], optimization: Doc) => updateDoc(doc(db, 'routes', routeId), { status: 'sent', stops, optimization });

/** Several routes not started, changed together (a stop moved from one to another). */
export async function reorderMany(changes: { routeId: string; stops: Doc[]; optimization: Doc }[], gone: string[] = []): Promise<void> {
  const b = writeBatch(db);
  for (const c of changes) b.update(doc(db, 'routes', c.routeId), { status: 'sent', stops: c.stops, optimization: c.optimization });
  for (const id of gone) b.delete(doc(db, 'routes', id));
  await b.commit();
}

/** Stops changed on routes waiting to start (Change… on a selection), in one write: their order and times stay as they are. */
export async function changeStops(changes: { routeId: string; stops: Doc[] }[]): Promise<void> {
  const b = writeBatch(db);
  for (const c of changes) b.update(doc(db, 'routes', c.routeId), { status: 'sent', stops: c.stops });
  await b.commit();
}

// ── Stops a business sends one at a time (SentStopsService.send) ─────────

export interface SendStop {
  businessId: string;
  riderId: string;
  kind: 'tryAgain' | 'bringBack' | 'handover' | 'pickup';
  stop: Doc;
  why?: string;
  fromRouteId?: string;
  fromStopId?: string;
  trackingCode?: string | null;
  holderId?: string | null;
  holderName?: string | null;
  holderPhone?: string | null;
  takerName?: string | null;
  takerPhone?: string | null;
  holderPhoto?: string | null;
  takerPhoto?: string | null;
  meet?: { label: string; lat: number; lng: number; holderStopId?: string | null; holderAt?: number | null; takerAt?: number | null } | null;
  /** Who has the parcel when it's sent, when it isn't [holderId] or the courier: `shop`. */
  holder?: string;
}

export function sentStopFields(s: SendStop): Doc {
  const opt = (k: string, v: unknown) => (v === undefined || v === null ? {} : { [k]: v });
  return {
    status: 'sent',
    businessId: s.businessId,
    sentBy: s.businessId,
    riderId: s.riderId,
    kind: s.kind,
    stop: { ...s.stop, status: 'pending', failureReason: null, completedAt: null },
    ...opt('why', s.why),
    ...opt('fromRouteId', s.fromRouteId),
    ...opt('fromStopId', s.fromStopId),
    ...opt('trackingCode', s.trackingCode),
    holderId: s.holderId ?? null,
    ...opt('holderName', s.holderName),
    ...opt('holderPhone', s.holderPhone),
    ...opt('takerName', s.takerName),
    ...opt('takerPhone', s.takerPhone),
    ...opt('holderPhoto', s.holderPhoto),
    ...opt('takerPhoto', s.takerPhoto),
    ...(s.meet
      ? {
          meet: {
            label: s.meet.label,
            lat: s.meet.lat,
            lng: s.meet.lng,
            ...opt('holderStopId', s.meet.holderStopId),
            ...(s.meet.holderAt != null ? { holderAt: Timestamp.fromMillis(s.meet.holderAt) } : {}),
            ...(s.meet.takerAt != null ? { takerAt: Timestamp.fromMillis(s.meet.takerAt) } : {}),
          },
        }
      : {}),
    // Who has the parcel: the courier who tried, until it changes hands (the shop, for a stop that was waiting there).
    ...(s.kind !== 'pickup' ? { holder: s.holder ?? s.holderId ?? s.riderId } : {}),
    sentAt: serverTimestamp(),
  };
}

export const newSentStopRef = () => doc(collection(db, 'sentStops'));
/** A stop sent on its own; a declined one for the same stop goes in the same write (SentStopsService.send `replaces`). */
export function sendStop(ref: DocumentReference, s: SendStop, replaces?: string | null): Promise<void> {
  const b = writeBatch(db);
  b.set(ref, sentStopFields(s));
  if (replaces) b.delete(doc(db, 'sentStops', replaces));
  return b.commit();
}
/**
 * Waiting stops sent one at a time to a courier on a route (SentStopsService.send), each with where it came from (so
 * Take back returns it to Waiting), leaving the list in the same write. In a transaction: if another tab sent or removed
 * any of them first, nothing is written.
 */
export function sendFromWaiting(businessId: string, items: { ref: DocumentReference; fields: SendStop; waitingId: string; fromWaiting: Doc }[]): Promise<void> {
  return runTransaction(db, async (tx) => {
    const refs = items.map((x) => doc(db, 'businesses', businessId, 'waitingStops', x.waitingId));
    const snaps = await Promise.all(refs.map((x) => tx.get(x)));
    if (snaps.some((x) => !x.exists())) throw new WaitingGone();
    items.forEach((x, i) => {
      tx.set(x.ref, { ...sentStopFields(x.fields), fromWaiting: x.fromWaiting });
      tx.delete(refs[i]);
    });
  });
}

/** "Take it back" for a stop sent on its own: only while it isn't on someone's route. One from Waiting goes back there ([restore]). */
export function takeBackStop(id: string, restore?: { businessId: string; items: { id: string; fields: Doc }[] }): Promise<void> {
  if (!restore?.items.length) return deleteDoc(doc(db, 'sentStops', id));
  const b = writeBatch(db);
  b.delete(doc(db, 'sentStops', id));
  for (const x of restore.items) b.set(doc(db, 'businesses', restore.businessId, 'waitingStops', x.id), x.fields);
  return b.commit();
}

// ── Zones (src/data/zones.ts) ─────────────────────────────────────────────

/** A new zone's reference, made before it's written so the page can select it. */
/** A courier's vehicle (`riders/{uid}.vehicleType`): the owner may change anything there but name and photo (firestore.rules). */
export const setCourierVehicle = (uid: string, vehicle: string) => updateDoc(doc(db, 'riders', uid), { vehicleType: vehicle });

/** On call or employed, as the phone's payment settings keep it (`businesses/{id}.payments.couriers.{uid}`). */
export const setCourierPay = (businessId: string, uid: string, onCall: boolean) =>
  updateDoc(doc(db, 'businesses', businessId), { [`payments.couriers.${uid}`]: onCall ? 'onCall' : 'employed' });

/** A courier's colour (one of the six by its place; null: automatic), as the phone keeps it (`businesses/{id}.couriers.{uid}`). */
export const setCourierColour = (businessId: string, uid: string, colour: number | null) =>
  updateDoc(doc(db, 'businesses', businessId), { [`couriers.${uid}.colour`]: colour ?? deleteField() });

/** The short name the business knows a courier by; null: their own name. */
export const setCourierShortName = (businessId: string, uid: string, name: string | null) =>
  updateDoc(doc(db, 'businesses', businessId), { [`couriers.${uid}.name`]: name ?? deleteField() });

/** Paused, with when it began (kept for billing); unpaused forgets both. */
export const setCourierPaused = (businessId: string, uid: string, paused: boolean) =>
  updateDoc(doc(db, 'businesses', businessId), {
    [`couriers.${uid}.paused`]: paused ? true : deleteField(),
    [`couriers.${uid}.pausedAt`]: paused ? serverTimestamp() : deleteField(),
  });

export const newZoneRef = (businessId: string) => doc(collection(db, 'businesses', businessId, 'zones'));

/** A zone written whole: `createdAt` is set only when it's new (the rules keep it after). */
export const saveZone = (ref: DocumentReference, fields: Doc, isNew: boolean) => (isNew ? setDoc(ref, fields) : updateDoc(ref, fields));

export const deleteZone = (businessId: string, id: string) => deleteDoc(doc(db, 'businesses', businessId, 'zones', id));

export const zoneRef = (businessId: string, id: string) => doc(db, 'businesses', businessId, 'zones', id);

// ── Where routes start (ProfileService.saveBusinessPlaces) ──────────────

/** The whole list, merged into the business's record with when it changed. Names and addresses only: no position. */
export const saveBusinessPlaces = (businessId: string, places: { label: string; address: string; placeId?: string; pin?: { lat: number; lng: number } }[]) =>
  setDoc(
    doc(db, 'businesses', businessId),
    { places: places.map((p) => ({ label: p.label, address: p.address, ...(p.placeId ? { placeId: p.placeId } : {}), ...(p.pin ? { pin: { lat: p.pin.lat, lng: p.pin.lng } } : {}) })), updatedAt: serverTimestamp() },
    { merge: true },
  );

// ── Needs a look: clear with a note (LiveAlertsService.clear) ────────────

export const CLEARABLE = new Set(['late', 'failedInARow', 'quiet', 'farFromAddress', 'unpaid', 'anotherDay', 'stillOpen', 'notAttempted', 'leftRoute']);

/** One record per alert and day (`{day}|{alertId}`): the rules take only new records, kept 30 days. */
export function clearAlert(a: { businessId: string; alertId: string; kind: string; day: string; note?: string }): Promise<void> {
  const text = a.note?.trim();
  return setDoc(doc(db, 'businesses', a.businessId, 'clearedAlerts', `${a.day}|${a.alertId}`), {
    kind: a.kind,
    ...(text ? { note: text.slice(0, 200) } : {}),
    day: a.day,
    at: serverTimestamp(),
    by: a.businessId,
    expiresAt: Timestamp.fromMillis(Date.now() + 30 * 24 * 60 * 60 * 1000),
  });
}

// ── Settling up (SettlingService) ────────────────────────────────────────

/** The owner confirms a hand-in the courier said they made, with what actually came in. */
export const confirmHandIn = (id: string, received: number, zeroThrough: number | null) =>
  updateDoc(doc(db, 'handIns', id), {
    status: 'confirmed',
    received,
    confirmedAt: serverTimestamp(),
    ...(zeroThrough != null ? { zeroThrough: Timestamp.fromMillis(zeroThrough) } : {}),
  });

/** It didn't come in: the money stays in the courier's balance, and both see it. */
export const handInNotReceived = (id: string) => updateDoc(doc(db, 'handIns', id), { status: 'notReceived', confirmedAt: serverTimestamp() });

/** Money handed over the counter, recorded by the owner (or what they paid an on-call courier: [out]). */
export const recordHandIn = (h: { businessId: string; riderId: string; amount: number; out: boolean; currency: string; how: 'cash' | 'transfer'; zeroThrough: number | null }) =>
  addDoc(collection(db, 'handIns'), {
    businessId: h.businessId,
    riderId: h.riderId,
    out: h.out,
    amount: h.amount,
    currency: h.currency,
    received: h.amount,
    how: h.how,
    status: 'confirmed',
    startedBy: h.businessId,
    at: serverTimestamp(),
    confirmedAt: serverTimestamp(),
    ...(h.zeroThrough != null ? { zeroThrough: Timestamp.fromMillis(h.zeroThrough) } : {}),
  });

// ── A route still open: ask the courier to end it (the `routeRemind` callable) ──

export async function remind(routeId: string): Promise<{ sent: boolean; remindedAt?: number; ended?: boolean }> {
  try {
    const r = await httpsCallable<{ routeId: string }, { sent?: boolean; remindedAt?: number }>(functions, 'routeRemind')({ routeId });
    return { sent: r.data.sent !== false, remindedAt: r.data.remindedAt };
  } catch (e) {
    if (String((e as { code?: string }).code ?? '').endsWith('failed-precondition')) return { sent: false, ended: true };
    throw e;
  }
}
